import * as Fathom from 'fathom-client';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import ReactGA from 'react-ga';
import Loadable from 'react-loadable';
import { Provider } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './utils/store';

// import configureStore from './utils/store';

import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import DefaultLandingRoute from './components/DefaultLandingRoute';
// import Footer from './components/Footer';
import ActivityLoader from './components/ActivityLoader';
import Parent from './components/Parent';
import ErrorBoundary from './pages/ErrorBoundary';
import NoMatch404 from './pages/NoMatch404';
import { ChatWidgetProvider } from './contexts/ChatWidgetContext';
import ChatWidget from './components/chat/ChatWidget';
import { isEnableDemoFeatures } from './utils/CTUtils';
import { injectStore } from './utils/http';
import QueryProvider from './QueryProvider';

// inject store to http api
injectStore(store);

const Login = Loadable({
  loader: () => import('./pages/Login'),
  loading: ActivityLoader,
});
const LoginToken = Loadable({
  loader: () => import('./pages/LoginToken'),
  loading: ActivityLoader,
});
const SSOLogin = Loadable({
  loader: () => import('./pages/SSOLogin'),
  loading: ActivityLoader,
});
const SSOReturn = Loadable({
  loader: () => import('./pages/SSOReturn'),
  loading: ActivityLoader,
});
const ConfirmUser = Loadable({
  loader: () => import('./pages/ConfirmUser'),
  loading: ActivityLoader,
});
const ResetPassword = Loadable({
  loader: () => import('./pages/ResetPassword'),
  loading: ActivityLoader,
});
const DocuSignReturn = Loadable({
  loader: () => import('./pages/DocuSignReturn'),
  loading: ActivityLoader,
});
const ForgotPassword = Loadable({
  loader: () => import('./pages/ForgotPassword'),
  loading: ActivityLoader,
});
const ChangePassword = Loadable({
  loader: () => import('./pages/ChangePassword'),
  loading: ActivityLoader,
});
const ContainerSearch = Loadable({
  loader: () => import('./pages/ContainerSearch'),
  loading: ActivityLoader,
});
const ContainerList = Loadable({
  loader: () => import('./pages/ContainerList'),
  loading: ActivityLoader,
});
const ContainerDashboard = Loadable({
  loader: () => import('./pages/ContainerDashboard'),
  loading: ActivityLoader,
});
const BillingDashboard = Loadable({
  loader: () => import('./pages/BillingDashboard'),
  loading: ActivityLoader,
});
const BookingDashboard = Loadable({
  loader: () => import('./pages/BookingDashboard'),
  loading: ActivityLoader,
});
const BlanketLOI = Loadable({
  loader: () => import('./pages/BlanketLOI'),
  loading: ActivityLoader,
});
const BlanketLOIDetails = Loadable({
  loader: () => import('./pages/BlanketLOIDetails'),
  loading: ActivityLoader,
});
const BookingRequest = Loadable({
  loader: () => import('./pages/BookingRequest'),
  loading: ActivityLoader,
});
const OcpRequest = Loadable({
  loader: () => import('./pages/OcpRequest'),
  loading: ActivityLoader,
});
const OcpRequestDetails = Loadable({
  loader: () => import('./pages/OcpRequestDetails'),
  loading: ActivityLoader,
});
const BookingRequestDetails = Loadable({
  loader: () => import('./pages/BookingRequestDetails'),
  loading: ActivityLoader,
});
const BookingUpdate = Loadable({
  loader: () => import('./pages/BookingUpdate'),
  loading: ActivityLoader,
});
const BookingUpdateDetails = Loadable({
  loader: () => import('./pages/BookingUpdateDetails'),
  loading: ActivityLoader,
});
const BookingHistory = Loadable({
  loader: () => import('./pages/BookingHistory'),
  loading: ActivityLoader,
});
const BookingCompare = Loadable({
  loader: () => import('./pages/BookingComparison'),
  loading: ActivityLoader,
});
const ShippingInstructionCompare = Loadable({
  loader: () => import('./pages/ShippingInstructionComparison'),
  loading: ActivityLoader,
});
const ContainerTracking = Loadable({
  loader: () => import('./pages/ContainerTracking'),
  loading: ActivityLoader,
});
const Registration = Loadable({
  loader: () => import('./pages/Registration'),
  loading: ActivityLoader,
});
const RegistrationInfo = Loadable({
  loader: () => import('./pages/RegistrationInfo'),
  loading: ActivityLoader,
});
const UserProfile = Loadable({
  loader: () => import('./pages/UserProfile'),
  loading: ActivityLoader,
});
const InfoCenter = Loadable({
  loader: () => import('./pages/InformationCenter'),
  loading: ActivityLoader,
});
const AdminProfileSearch = Loadable({
  loader: () => import('./pages/AdminProfileSearch'),
  loading: ActivityLoader,
});
const AdminProfileSearchDetails = Loadable({
  loader: () => import('./pages/AdminProfileSearchDetails'),
  loading: ActivityLoader,
});
const AdminAccountSearch = Loadable({
  loader: () => import('./pages/AdminAccountSearch'),
  loading: ActivityLoader,
});
const AdminAccountSearchDetails = Loadable({
  loader: () => import('./pages/AdminAccountSearchDetails'),
  loading: ActivityLoader,
});
const SupportHelp = Loadable({
  loader: () => import('./pages/SupportHelp'),
  loading: ActivityLoader,
});
const ShippingInstructions = Loadable({
  loader: () => import('./pages/ShippingInstructions'),
  loading: ActivityLoader,
});
const ShippingInstructionsDetails = Loadable({
  loader: () => import('./pages/ShippingInstructionsDetails'),
  loading: ActivityLoader,
});
const ShippingInstructionsTemplateDetails = Loadable({
  loader: () => import('./pages/ShippingInstructionsTemplateDetails'),
  loading: ActivityLoader,
});
const AdminEtaRuleSearch = Loadable({
  loader: () => import('./pages/AdminEtaRuleSearch'),
  loading: ActivityLoader,
});
const AdminEtaRuleSearchDetails = Loadable({
  loader: () => import('./pages/AdminEtaRuleSearchDetails'),
  loading: ActivityLoader,
});

const SalesView = Loadable({
  loader: () => import('./pages/SalesView'),
  loading: ActivityLoader,
});
const RepSalesView = Loadable({
  loader: () => import('./pages/RepSalesView'),
  loading: ActivityLoader,
});

const AdminEtaVvdExSearch = Loadable({
  loader: () => import('./pages/AdminEtaVvdExSearch'),
  loading: ActivityLoader,
});
const AdminEtaVvdExSearchDetails = Loadable({
  loader: () => import('./pages/AdminEtaVvdExSearchDetails'),
  loading: ActivityLoader,
});

const VGMDetails = Loadable({
  loader: () => import('./pages/VGMDetails'),
  loading: ActivityLoader,
});

const DetentionChargeCalculator = Loadable({
  loader: () => import('./pages/DetentionChargeCalculator'),
  loading: ActivityLoader,
});

const DomesticShipmentHub = Loadable({
  loader: () => import('./components/domestic/DomesticShipmentHub'),
  loading: ActivityLoader,
});
const DomesticBookingHub = Loadable({
  loader: () => import('./components/domestic/DomesticBookingHub'),
  loading: ActivityLoader,
});
const DomesticIntlShipmentHub = Loadable({
  loader: () => import('./components/domestic/DomesticIntlShipmentHub'),
  loading: ActivityLoader,
});
const DomesticShippingInstructionHub = Loadable({
  loader: () =>
    import('./components/domestic/DomesticShippingInstructionHub'),
  loading: ActivityLoader,
});
const DomesticAccountStatementHub = Loadable({
  loader: () => import('./components/domestic/DomesticAccountStatementHub'),
  loading: ActivityLoader,
});
const DomesticDocumentHub = Loadable({
  loader: () => import('./components/domestic/DomesticDocumentHub'),
  loading: ActivityLoader,
});

function DomesticLegacyTrackingRedirect() {
  return <Redirect to="/d/shipment/dashboard" />;
}

// import styles from './App.module.css';

global.BMapKey = process.env.REACT_APP_BING_MAP_KEY;

//  User Directory Service APIs
global.URL_UserReg = process.env.REACT_APP_USER_API_URL + '/dir/user';
global.URL_UserConf =
  process.env.REACT_APP_USER_API_URL + '/dir/user/confirmation';
global.URL_UserForgot = process.env.REACT_APP_USER_API_URL + '/dir/user/forgot';
global.URL_ResetPass =
  process.env.REACT_APP_USER_API_URL + '/dir/user/confirmforgot';
global.URL_Login = process.env.REACT_APP_USER_API_URL + '/dir/login';
global.URL_SSO_Callback =
  process.env.REACT_APP_USER_API_URL + '/dir/sso/callback';
global.ENABLE_SSO_LOGIN =
  String(process.env.REACT_APP_ENABLE_SSO_LOGIN || 'false').toLowerCase() === 'true';
global.URL_CompleteNewPassword =
  process.env.REACT_APP_USER_API_URL + '/dir/complete-new-password';
global.URL_TokenRefresh =
  process.env.REACT_APP_USER_API_URL + '/dir/login/refresh';
global.URL_Logout = process.env.REACT_APP_USER_API_URL + '/dir/logout';
global.URL_ChangePass = process.env.REACT_APP_USER_API_URL + '/dir/user/change';
global.URL_ResendCode = process.env.REACT_APP_USER_API_URL + '/dir/user/resend';

// User Management Service APIs
global.URL_UserProfileLookupUpdate =
  process.env.REACT_APP_USER_API_URL + '/mgmt/cp/user';
global.URL_AdminUserReset =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/reset';
global.URL_AdminUserSetPassword =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/pwd/set';
global.URL_AdminEmailLookUp =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/email';
global.URL_AdminUserSearch =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/user';
global.URL_AdminUserDetail =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/authEvents';
global.URL_AdminOrgLookUp =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/cp/org';
global.URL_AdminProfileSearch =
  process.env.REACT_APP_USER_API_URL + '/mgmt/admin/cp/user';
global.URL_UserAssume =
  process.env.REACT_APP_USER_API_URL + '/mgmt/user/assume';

// CargoTrack API's
global.URL_Base = process.env.REACT_APP_CARGO_API_URL + '/cargotrack/';
global.V2_URL_Base = process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/';
global.URL_Base_Proxy = global.V2_URL_Base + 'gl/';
global.URL_Base_Public_Proxy = global.V2_URL_Base + 'glusp/v/';
global.URL_SearchByBooking =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo?';
global.URL_SearchByBookingV2 =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/cargo?';
// Domestic shipment-tracking short/full Excel (POST, binary via secured proxy)
global.URL_CargoSearchShortExcel =
  global.V2_URL_Base + 'cargo/search/shortexcel';
global.URL_CargoSearchFullExcel =
  global.V2_URL_Base + 'cargo/search/fullexcel';
global.URL_SearchDetails =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/detail?';
global.URL_SearchByBookingNoAuth =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargopub?';
global.URL_SearchDetailsNoAuth =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargopub/detailpub?';
global.URL_GetVVDs =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/vvds';
global.URL_GetVVDsAll =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/vvds/all';
global.URL_GetCardData =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/dashboard/';
global.URL_GetLocation =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/location?';
global.URL_BookingSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings';
global.URL_OcpSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/requests';
global.URL_OcpRequest =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/request';
global.URL_OcpSave =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/save';
global.URL_OcpVerify =
process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/verify';
global.URL_OcpCancel =
process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/cancel';
global.URL_OcpLocationRefData =
 process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/refdata/locations';
global.URL_OcpLocationDepotRefData =
 process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/refdata/location-depots';
global.URL_BookingRequestRefData =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/refdata';
global.URL_BookingRequestRefDataNoAuth =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/pub/refdata/';
global.URL_TranslateRefData =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/translateRefData';
global.URL_BookingDetail =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking';
global.URL_BookingUpdateDetail =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/view'; // '/gates/booking' /gates-service/api/v1/booking/
global.URL_BookingSaveDraft =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/draft';
global.URL_BookingConfirmation =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/confirmation';
global.URL_VesselSearchByVVD =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/vessel/vvd?';
global.URL_VesselSearchByDate =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/vessel/date?';
global.URL_GetBRGridPreference =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/prefs?';
global.URL_ViewBooking =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/domesticbooking/view-booking';
global.URL_DomesticBookingSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/domesticbooking/search';
//Upload file to S3 is commented out - logic to move to backend
// global.URL_BookingUpload =
//   process.env.REACT_APP_BOOKING_UPLOAD_URL + '/cargotrack/edi/form/url';
global.URL_ShippingSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions';
global.URL_BiSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/search';
global.URL_BiBooking =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/booking';
global.URL_BiInstruction =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions';
global.URL_BiSubmit =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions';
global.URL_BiValidate =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/validate';
global.URL_BiPreview =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/preview';
global.URL_BookingRefValidation =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings/validate';
global.URL_ShippingDetail =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instruction';
global.URL_ShippingContainerLookup =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/containers';
global.URL_CustomsHistory =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/cargo/customs/history';
global.URL_ShippingSaveDraft =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/draft';
global.URL_ShippingSubmit =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si';
global.URL_SITemplateCreate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/si/template/create';
global.URL_SITemplateList =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/si/template/list';
global.URL_DomesticBookingTemplateList =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/templates';
global.URL_DomesticBookingLoad =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/load/v2';
global.URL_DomesticBookingRenameMyTemplate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/rename-my-template';
global.URL_DomesticBookingSubmit =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/submit';
global.URL_DomesticBookingValidate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/validate';
global.URL_DomesticBookingDraft =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/draft';
global.URL_DomesticBookingCancel =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/cancel';
global.URL_DomesticBookingReactivate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/reactivate';
global.URL_DomesticBookingSaveAsTemplate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/save-as-template';
global.URL_DomesticBookingVesselItineraries =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/vessel-itineraries';
global.URL_DomesticBookingVesselItinerariesV2 =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/vessel-itineraries/v2';
global.URL_DomesticBookingDecodeVin =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/decode-vin';
global.URL_DomesticBookingCalculatePickupDate =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/calculate-pickup-date';
global.URL_DomesticBookingPovBlockDates =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/pov-block-dates';
global.URL_DomesticBookingEquipmentRef =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/equipment';
global.URL_DomesticBookingPortCities =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/cities';
global.URL_DomesticBookingLoadDischargeRef =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/load-discharge';
global.URL_DomesticBookingRailRamps =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/rail-ramps';
global.URL_DomesticBookingPackageTypes =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/package-types';
global.URL_DomesticBookingSpotAddresses =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/spot-addresses';
global.URL_DomesticBookingDetailsDashboardSearch =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/dashboard/search';
global.URL_DomesticBookingDetailsDashboardBooking =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/dashboard/bookings';
global.URL_DomesticAccountBalancesSummary =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/accountbalances/summary';
global.URL_DomesticAccountBalancesDetails =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/accountbalances/details';
global.URL_DomesticBookingSpotAddressesExpand =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/spot-addresses/expand';
// AutoTrack (Track Autos) APIs - POST, served under the V2 base
// (https://api.cargo.<env>.matson.com/v2/cargotrack/ocean/tracking/autotrack/*)
global.URL_TrackAutosReport =
  global.V2_URL_Base + 'ocean/tracking/autotrack/report';
global.URL_TrackAutosTotals =
  global.V2_URL_Base + 'ocean/tracking/autotrack/totals';
global.URL_TrackAutosInlandTotals =
  global.V2_URL_Base + 'ocean/tracking/autotrack/inland-totals';
global.URL_TrackAutosExport =
  global.V2_URL_Base + 'ocean/tracking/autotrack/export';
global.URL_TrackAutosStatusOptions =
  global.V2_URL_Base + 'ocean/tracking/autotrack/status-options';
global.URL_BookingTemplateList =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/templates';
global.URL_BookingSaveSubmit =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings/forms';
global.URL_SIFormDownload =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/form';
global.URL_SIBLDraft =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/bldraft';
global.URL_SIBLDocsSend =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/blDocsSend';
global.URL_SIExcelSaveSubmit =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/forms';
global.URL_SIExcelValidate =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/validate';
global.URL_SIExcelParseUpload =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/xlsparse';
global.countryCode = process.env.REACT_APP_COUNTRY_CODE;
global.URL_GetExtTokenInfo =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/ext/gettoken';
global.URL_ManualAddition =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/manual';
global.URL_AllocationHistory =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/report/download';
global.URL_HISTORICAL_VVD_REF =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/historical-vvd';
// ETA apis
global.URL_EtaRule_Search =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/searchPost';
global.URL_EtaRule_Details =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/createUpdate';

global.URL_EtaExceptionRule_Search =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/exception/search';
global.URL_EtaExceptionRule_Details =
  process.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/exception/createUpdate';
// VGM apis
global.URL_VGMGetCarrierBookingTypes =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/getcarriers';
global.URL_VGMSearchByBooking =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/search';
global.URL_VGMSaveBkEquipments =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/save';
global.URL_VGMSaveMultipleBkEquipments =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vgm/saveMultipleBooking';
global.URL_VGMValidContainer =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/isvalidcontainer';

// Bldoc apis
global.URL_BldocSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/search';
global.URL_BldocOTLoiSubmit =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/onetimeloi';
global.URL_BldocBlanketLoiSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/search';
global.URL_BldocBlanketLoiAdd =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/add';
global.URL_BldocBlanketLoiUpdate =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/update';
global.URL_BldocBlanketLoiGet =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/get';
global.URL_BldocBlanketLoiTariff =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/tariff';
global.URL_BldocDownload =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/download';
global.URL_BldocStatusHistory =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/statushistory';
global.URL_BldocDownloadUrl =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/downloadloi';
global.URL_BldocBlanketShipperApproval =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/blanket';
global.URL_BldocOnetimeShipperApproval =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/onetime';
global.URL_BldocSignComplete =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/complete';
global.URL_BldocSignCheckCallbackStatus =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/checkcallback';
global.URL_BldocSignCheckgroup =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/checkgroup';

global.URL_SalesViewSearch =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/search';
global.URL_SalesViewUpdate =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/update';
global.URL_SalesViewRepVVD =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/vvd';
global.URL_SalesViewRepPort =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/pol';
global.URL_SalesViewRepSalesRep =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/salesRep';
global.URL_RepSalesViewAccountName =
  process.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/account';
global.URL_RepSalesViewSearch =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/search';
global.URL_RepSalesViewUpdate =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/update';
global.URL_RepSalesViewDownload =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/download';
global.URL_RepSalesViewDownloadV2 =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/download/v2';
global.URL_RepSalesViewUpload =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/upload';
global.URL_RepSalesViewUploadV2 =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/upload/v2';
global.URL_RepSalesViewNotify =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/notify';
global.URL_RepSalesViewHistory =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/history';
global.URL_RepSalesViewRepDefault =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/salesRep/default';
global.URL_EirHistory =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/booking/eir/history';
global.URL_BookingCorrSearch =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/search';
global.URL_BookingCorrHistory =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/history';
global.URL_BookingCorrCompare =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/compare';
global.URL_BookingCorrList =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/';
// global.URL_ShippingInstructionCorrCompare =
//   process.env.REACT_APP_CARGO_API_URL +
//   '/v2/cargotrack/gl/siRequest/compare';
global.URL_ShippingInstructionVersionList =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/siRequest/version';
global.URL_ShippingInstructionCorrApprove =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/siRequest/siCorrApprove';
global.URL_BookingCorrController =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/action';
global.URL_BookingCorrControllerUpdate =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/update';
global.URL_ShippingInstructionCorrCompare =
  process.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/siRequest/compare';
global.URL_SiCustomsDocDownload =
  process.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/dl/siRequest/downloadCustomsDoc';
global.URL_BookingCorrCancelBooking =
  process.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/deleteBooking";
global.URL_BookingCorrReApproveBooking =
  process.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/reApproveBooking";
global.URL_BookingCorrPermissions =
  process.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/checkPermission";
global.URL_BookingCorrIsLatest =
  process.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/checkLatestBookingData";
global.URL_AVPNotificationSubscription =
  process.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/pubapi/v1/trucker/subscriptions";
// Detention Charge Calc API/s
global.URL_DETENTION_RATE =
  process.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/detention/rate";
global.URL_DETENTION_RATE_PUBLIC =
  process.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/detention/rate/pub";
global.URL_ServiceContractRefData =
  process.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/gl/detention/svcContract";
global.URL_VGMRefValidation =
  process.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/gl/vgm/validate";
global.URL_Health = global.URL_Base_Public_Proxy + "util/health";
//
//
// load analytics service based on country.
// Needed because GA does not work in CN
//
if (
  'CN' === process.env.REACT_APP_COUNTRY_CODE ||
  'sandbox' === process.env.REACT_APP_STAGE
) {
  console.debug('- load fathom/');
  Fathom.load(process.env.REACT_APP_FATHOM_SITE_ID);
}
if ('US' === process.env.REACT_APP_COUNTRY_CODE) {
  console.debug('- init ga');
  ReactGA.initialize(process.env.REACT_APP_GA_TRACKER);
}

global.trackAnalyticsPageView = (pageurl) => {
  if (process.env.REACT_APP_COUNTRY_CODE === 'CN') {
    console.info('- track fathom/ page view');
    Fathom.trackPageview({ pageurl });
  } else {
    console.info('- track ga page view');
    ReactGA.pageview(pageurl);
  }
};

global.trackAnalyticsEvent = (category, action, pageurl, user) => {
  console.debug(
    `Analytics Event - category: ${category} action: ${action} pageurl: ${pageurl} user: ${user}`
  );
  if (process.env.REACT_APP_COUNTRY_CODE === 'CN') {
    console.info('- send fathom/ event');
    console.log(`---- action: ${action}`);
    Fathom.trackGoal(action, 0);
  } else {
    console.info('- send ga event');
    ReactGA.event({ category, action, label: user });
  }
};

global.updateUserObject = (user, response) => {
  let newUser = _.cloneDeep(user);
  newUser.idToken = response.IdToken;
  newUser.accessToken = response.AccessToken;
  newUser.expiresIn = response.ExpiresIn;
  newUser.tokenType = response.TokenType;
  newUser.loginTimeStamp = moment().toDate();
  if (response.RefreshToken) {
    newUser.refreshToken = response.RefreshToken;
  }
  return newUser;
};

const App = () => {
  //useEffect(() => {
  //    document.title = "Matson Cargo Portal " + "("+ store.getState().appinfo.instanceId + ")";
  //  }, []);

  return (
    <Provider store={store}>
      <QueryProvider>
      <PersistGate loading={null} persistor={persistor}>
        <Router>
          <ChatWidgetProvider>
            <Parent>
              <Header />
              <ErrorBoundary>
                <Switch>
                <Route exact path="/" component={DefaultLandingRoute} />
                <Route exact path="/signin" component={Login} />
                <Route
                  exact
                  path="/ssologin"
                  render={(props) =>
                    global.ENABLE_SSO_LOGIN ? (
                      <SSOLogin {...props} />
                    ) : (
                      <Redirect
                        to={{
                          pathname: '/signin',
                          state: {
                            ssoError:
                              'SSO login is disabled for this environment. Please use username/password login.',
                          },
                        }}
                      />
                    )
                  }
                />
                <Route
                  exact
                  path="/sso/okta"
                  render={(props) =>
                    global.ENABLE_SSO_LOGIN ? (
                      <SSOReturn {...props} />
                    ) : (
                      <Redirect to="/signin" />
                    )
                  }
                />
                <Route
                  exact
                  path="/detentionChargeCalculator"
                  component={DetentionChargeCalculator}
                />
                <Route
                  exact
                  path="/signintoken/:username/:token"
                  component={LoginToken}
                />
                <Route
                  exact
                  path="/confirmUser/:username"
                  component={ConfirmUser}
                />
                <Route
                  exact
                  path="/confirmUser/:username/:code"
                  component={ConfirmUser}
                />
                <Route
                  exact
                  path="/resetPassword/:username"
                  component={ResetPassword}
                />
                <Route
                  exact
                  path="/resetPassword/:username/:code"
                  component={ResetPassword}
                />
                <Route
                  exact
                  path="/forgetPassword"
                  component={ForgotPassword}
                />
                <Route exact path="/registration" component={Registration} />
                <Route exact path="/registration-info" component={RegistrationInfo} />
                <Route
                  exact
                  path="/informationCenter"
                  component={InfoCenter}
                />
                <Route
                  exact
                  path="/containerList/:searchType=:searchQuery"
                  component={ContainerList}
                />
                <Route
                  exact
                  path="/containerDetails/:bookingNo/:containerNo"
                  component={ContainerTracking}
                />
                <Route exact path="/vgmDetails" component={VGMDetails} />
                <Route
                  exact
                  path="/docuSignReturn"
                  component={DocuSignReturn}
                />
                <ProtectedRoute
                  exact
                  path="/search"
                  component={ContainerSearch}
                />
                <ProtectedRoute
                  exact
                  path="/containerdash"
                  component={ContainerDashboard}
                />
                <ProtectedRoute
                  exact
                  path="/billingdoc"
                  component={BillingDashboard}
                />
                <ProtectedRoute exact path="/salesView" component={SalesView} />
                <ProtectedRoute
                  exact
                  path="/repSalesView"
                  component={RepSalesView}
                />
                <ProtectedRoute
                  exact
                  path="/bookingdash"
                  component={BookingDashboard}
                />
                <ProtectedRoute
                  exact
                  path="/blanketloi"
                  component={BlanketLOI}
                />
                <ProtectedRoute
                  exact
                  path="/blanketloidetails"
                  component={BlanketLOIDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookingRequest"
                  component={BookingRequest}
                />
                {isEnableDemoFeatures() && (
                  <ProtectedRoute
                    exact
                    path="/ocpRequest"
                    component={OcpRequest}
                  />
                )}
                {isEnableDemoFeatures() && (
                  <ProtectedRoute
                    exact
                    path="/ocpRequestDetails"
                    component={OcpRequestDetails}
                  />
                )}
                <ProtectedRoute
                  exact
                  path="/bookingRequestDetails"
                  component={BookingRequestDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookingUpdate"
                  component={BookingUpdate}
                />
                <ProtectedRoute
                  exact
                  path="/bookingUpdateDetails"
                  component={BookingUpdateDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookinghistory"
                  component={BookingHistory}
                />
                <ProtectedRoute
                  exact
                  path="/bookingCompare"
                  component={BookingCompare}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionCompare"
                  component={ShippingInstructionCompare}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructions"
                  component={ShippingInstructions}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionsDetails"
                  component={ShippingInstructionsDetails}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionsTemplateDetails"
                  component={ShippingInstructionsTemplateDetails}
                />
                <ProtectedRoute exact path="/profile" component={UserProfile} />
                <ProtectedRoute
                  exact
                  path="/adminAccountSearch"
                  component={AdminAccountSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminAccountSearchDetails/:username"
                  component={AdminAccountSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/adminProfileSearch"
                  component={AdminProfileSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminProfileSearchDetails/:username"
                  component={AdminProfileSearchDetails}
                />
                <ProtectedRoute exact path="/support" component={SupportHelp} />
                <ProtectedRoute
                  exact
                  path="/changePassword"
                  component={ChangePassword}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleSearch"
                  component={AdminEtaRuleSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleSearchDetails/:ruleId"
                  component={AdminEtaRuleSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleExceptionSearch"
                  component={AdminEtaVvdExSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleExceptionDetails/:ruleId"
                  component={AdminEtaVvdExSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/detentionChargeCalculator"
                  component={DetentionChargeCalculator}
                />
                <ProtectedRoute
                  path="/d/booking"
                  component={DomesticBookingHub}
                />
                <ProtectedRoute
                  path="/d/shipping-instruction"
                  component={DomesticShippingInstructionHub}
                />
                <ProtectedRoute
                  path="/d/billingdocs"
                  component={DomesticDocumentHub}
                />
                <ProtectedRoute
                  path="/d/shipment"
                  component={DomesticShipmentHub}
                />
                <ProtectedRoute
                  path="/d/intl"
                  component={DomesticIntlShipmentHub}
                />
                <ProtectedRoute
                  path="/d/account-statement"
                  component={DomesticAccountStatementHub}
                />
                <ProtectedRoute
                  exact
                  path="/d/tracking"
                  component={DomesticLegacyTrackingRedirect}
                />
                <Route path="*" component={NoMatch404} />
              </Switch>
              </ErrorBoundary>
              <ChatWidget />
            </Parent>
          </ChatWidgetProvider>
        </Router>
      </PersistGate>
      </QueryProvider>
    </Provider>
  );
};

export default App;
